import { ReactElement, useState, useCallback, useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useRoutes, useAwsEffect, useAwsGetter, useAwsBatchIterator } from '@localstack/services';
import { Search as SearchIcon } from '@mui/icons-material';
import { Card, Button, Box, InputAdornment, TextField } from '@mui/material';

import { Dropdown, ButtonWithDialog, ECSServicesTable, PageTitle } from '@localstack/ui';

import { DEFAULT_ECS_ROUTES } from '@localstack/constants';

import { ECSProps } from './types';

import { ClusterNavTabs } from './components';

const POLLING_INTERVAL = 5000;

export const ECSClusterServices = ({
  Layout,
  clientOverrides,
  routes = DEFAULT_ECS_ROUTES,
}: ECSProps): ReactElement => {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  const { clusterName } = useParams<'clusterName'>();

  const { goto } = useRoutes();
  const { describeServices } = useAwsEffect('ECS', ['describeServices'], { clientOverrides });

  const {
    data: serviceArns,
    isLoading,
    mutate,
  } = useAwsGetter('ECS', 'listServices', [{ cluster: clusterName }], {
    swrOverrides: { refreshInterval: POLLING_INTERVAL },
    clientOverrides,
  });

  const services = useAwsBatchIterator(
    serviceArns?.serviceArns,
    async (arn) => (await describeServices({ cluster: clusterName, services: [arn] }))?.services ?? [],
  );

  const normalizedSearchQuery = searchQuery.trim().toLocaleLowerCase();
  const filteredServices = useMemo(
    () =>
      services.filter(({ serviceName = '', serviceArn = '' }) => {
        if (!normalizedSearchQuery) {
          return true;
        }

        return [serviceName, serviceArn].some((value) => value.toLocaleLowerCase().includes(normalizedSearchQuery));
      }),
    [normalizedSearchQuery, services],
  );
  const visibleServiceNames = useMemo(
    () => new Set(filteredServices.map(({ serviceName }) => serviceName).filter(Boolean)),
    [filteredServices],
  );

  useEffect(() => {
    setSelectedIds((current) => {
      const next = current.filter((id) => visibleServiceNames.has(id));
      return next.length === current.length ? current : next;
    });
  }, [visibleServiceNames]);

  const { deleteService } = useAwsEffect('ECS', ['deleteService'], {
    revalidate: ['listServices', 'describeServices'],
    clientOverrides,
  });

  const handleDeleteSelected = useCallback(async () => {
    const promises = selectedIds.map((service) => deleteService({ cluster: clusterName, service }));
    await Promise.all(promises);
  }, [clusterName, deleteService, selectedIds]);

  return (
    <Layout
      documentTitle="Cluster Services"
      tabs={<ClusterNavTabs clusterName={clusterName as string} routes={routes} />}
      title={
        <PageTitle
          title="Cluster Services"
          onMutate={mutate}
          breadcrumbs={[
            ['ECS', () => goto(routes.RESOURCES_ECS)],
            ['Clusters', () => goto(routes.RESOURCES_ECS_CLUSTERS)],
            [clusterName, () => goto(routes.RESOURCES_ECS_CLUSTER, { clusterName })],
            ['Services', null],
          ]}
        />
      }
      actions={
        <>
          <Button variant="outlined" onClick={() => goto(routes.RESOURCES_ECS_CLUSTER_SERVICE_CREATE, { clusterName })}>
            Create Service
          </Button>
          <Dropdown label="Actions">
            <ButtonWithDialog
              componentType="MenuItem"
              disabled={selectedIds.length === 0 || isLoading}
              dialogTitle={`Delete ${selectedIds.length} Service(s)?`}
              onClick={handleDeleteSelected}
              dialogContent="Selected Services will be permanently deleted"
            >
              Remove Selected
            </ButtonWithDialog>
          </Dropdown>
        </>
      }
    >
      <Card>
        <Box px={2} pt={2}>
          <TextField
            fullWidth
            size="small"
            value={searchQuery}
            placeholder="Search services by name or ARN"
            onChange={(event) => setSearchQuery(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <ECSServicesTable
          services={filteredServices}
          loading={isLoading}
          onSelect={setSelectedIds}
          onViewService={({ serviceArn }) =>
            goto(routes.RESOURCES_ECS_CLUSTER_SERVICE, { clusterName, arn: serviceArn })
          }
        />
      </Card>
    </Layout>
  );
};
